// src/pages/problem-bank/ReviewSubmission.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import {
  ArrowLeft,
  Save,
  XCircle,
  PlusCircle,
  AlertTriangle,
  CheckCircle,
  Building2,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  DollarSign,
  Target,
  FileText,
  Info,
  Loader2,
  ThumbsDown,
  Eye,
  Shield,
  RefreshCw,
  UserCheck,
  Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type SubmissionType = 'company' | 'public';

interface BaseSubmission {
  id: string;
  type: SubmissionType;
  status: string;
  submittedAt: string;
  reviewedAt?: string;
  reviewNotes?: string;
  convertedToProblemId?: string;
  assignedTo?: string;
  assignedToName?: string;
  assignedAt?: string;
}

interface CompanySubmission extends BaseSubmission {
  type: 'company';
  companyName: string;
  companyRegistration?: string;
  industrySector?: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  problemTitle: string;
  problemDescription: string;
  category: string[];
  urgency: string;
  estimatedImpact?: string;
  totalAnnualCost?: number;
  quantification?: any;
  rootCause?: any;
  pilotReadiness?: any;
  technicalInfo?: any;
  documents?: string[];
}

interface PublicSubmission extends BaseSubmission {
  type: 'public';
  fullName: string;
  email: string;
  phone?: string;
  organization?: string;
  problemTitle: string;
  problemDescription: string;
  industrySector?: string;
  category?: string[];
  urgency: string;
  location?: string;
  estimatedImpact?: string;
  documents?: string[];
}

interface ProblemEnrichment {
  totalLossPerYear: number;
  confidenceLevel: 'low' | 'medium' | 'high';
  calculationMethodology: string;
  dataSources: string[];
  rootCauseCategory: string;
  rootCauseDetails: string;
  fiveWhys: Array<{ level: number; question: string; answer: string }>;
  barriersToInternalSolution: string[];
  hypothesis: string;
  solutionType: 'software' | 'hardware' | 'hybrid' | 'service';
  keyFeatures: string[];
  mvpFeatures: string[];
  estimatedDevCost: number;
  estimatedDevTimeline: number;
  estimatedDeployCost: number;
  estimatedPriceModel: string;
  anchorPaybackMonths: number;
  marketSizeKenya: number;
  marketSizeEastAfrica: number;
  competitorLandscape: string;
  scalabilityScore: number;
  anchorUrgencyScore: number;
  willingToPay: 'yes' | 'maybe' | 'no';
  loiPotential: number;
}

const CONFIDENCE_LEVELS = [
  { value: 'low', label: 'Low - Rough estimate' },
  { value: 'medium', label: 'Medium - Some data available' },
  { value: 'high', label: 'High - Verified data' }
];

const SOLUTION_TYPES = [
  { value: 'software', label: 'Software Only' },
  { value: 'hardware', label: 'Hardware Only' },
  { value: 'hybrid', label: 'Hybrid (Software + Hardware)' },
  { value: 'service', label: 'Service/Consulting' }
];

const ROOT_CAUSE_CATEGORIES = [
  'Process Inefficiency',
  'Equipment Failure',
  'Labor/Workforce',
  'Supply Chain',
  'Quality Control',
  'Energy Waste',
  'Data/Systems',
  'Regulatory/Compliance',
  'Other'
];

const URGENCY_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200'
};

const STATUS_BADGE_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  assigned: 'bg-purple-100 text-purple-700 border-purple-200',
  under_review: 'bg-blue-100 text-blue-700 border-blue-200',
  converted: 'bg-gray-100 text-gray-700 border-gray-200',
  rejected: 'bg-red-100 text-red-700 border-red-200'
};

export default function ReviewSubmission() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();

  const isProblemBankAnalyst = user?.analyst_type === 'problem_bank' || user?.role === 'senior_analyst';
  const isSeniorAnalyst = user?.role === 'senior_analyst';
  const isBusinessAnalyst = user?.analyst_type === 'business';

  const [submission, setSubmission] = useState<CompanySubmission | PublicSubmission | null>(null);
  const [problemData, setProblemData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('quantification');
  const [isConverted, setIsConverted] = useState(false);
  const [convertedProblemId, setConvertedProblemId] = useState<string | null>(null);
  const [isRejected, setIsRejected] = useState(false);
  const [availableAnalysts, setAvailableAnalysts] = useState<any[]>([]);
  const [reassignmentDialogOpen, setReassignmentDialogOpen] = useState(false);
  const [selectedAnalystId, setSelectedAnalystId] = useState<string>('');
  const [isReassigning, setIsReassigning] = useState(false);

  const [enrichment, setEnrichment] = useState<ProblemEnrichment>({
    totalLossPerYear: 0,
    confidenceLevel: 'medium',
    calculationMethodology: '',
    dataSources: [],
    rootCauseCategory: 'Other',
    rootCauseDetails: '',
    fiveWhys: [
      { level: 1, question: 'Why does this problem occur?', answer: '' },
      { level: 2, question: 'Why?', answer: '' },
      { level: 3, question: 'Why?', answer: '' },
      { level: 4, question: 'Why?', answer: '' },
      { level: 5, question: 'What is the root cause?', answer: '' }
    ],
    barriersToInternalSolution: [],
    hypothesis: '',
    solutionType: 'hybrid',
    keyFeatures: [],
    mvpFeatures: [],
    estimatedDevCost: 0,
    estimatedDevTimeline: 0,
    estimatedDeployCost: 0,
    estimatedPriceModel: 'Subscription',
    anchorPaybackMonths: 0,
    marketSizeKenya: 0,
    marketSizeEastAfrica: 0,
    competitorLandscape: '',
    scalabilityScore: 3,
    anchorUrgencyScore: 3,
    willingToPay: 'maybe',
    loiPotential: 2
  });

  const [tempDataSource, setTempDataSource] = useState('');
  const [tempBarrier, setTempBarrier] = useState('');
  const [tempKeyFeature, setTempKeyFeature] = useState('');
  const [tempMVPFeature, setTempMVPFeature] = useState('');

  if (isBusinessAnalyst) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground text-center max-w-md">
          Reviewing submissions is only accessible to Problem Bank Analysts and Senior Analysts.
        </p>
        <Button className="mt-6" onClick={() => navigate('/analyst-dashboard')}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  const getRootCauseDetails = (rootCause: any): string => {
    if (!rootCause) return 'Needs investigation';
    if (typeof rootCause === 'string') return rootCause;
    if (rootCause.details) return rootCause.details;
    if (rootCause.rootCauseDetails) return rootCause.rootCauseDetails;
    if (rootCause.category) return `${rootCause.category}: ${rootCause.details || rootCause.rootCauseDetails || 'Needs investigation'}`;
    return JSON.stringify(rootCause, null, 2);
  };

  const loadAvailableAnalysts = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, analyst_type')
        .eq('analyst_type', 'problem_bank')
        .in('role', ['analyst', 'senior_analyst']);

      if (error) throw error;
      setAvailableAnalysts(data || []);
    } catch (error) {
      console.error('Error loading analysts:', error);
    }
  };

  const loadProblemData = async (problemId: string) => {
    try {
      const { data, error } = await supabase
        .from('problem_bank')
        .select('*')
        .eq('id', problemId)
        .single();

      if (error) throw error;

      if (data) {
        const quantification = data.quantification || {};
        const rootCauseAnalysis = data.root_cause_analysis || {};
        const solutionPotential = data.solution_potential || {};
        const verification = data.verification || {};

        setEnrichment({
          totalLossPerYear: quantification.totalLossPerYear || 0,
          confidenceLevel: quantification.confidenceLevel || 'medium',
          calculationMethodology: quantification.calculationMethodology || '',
          dataSources: quantification.dataSources || [],
          rootCauseCategory: rootCauseAnalysis.rootCauseCategory || 'Other',
          rootCauseDetails: rootCauseAnalysis.rootCauseDetails || '',
          fiveWhys: rootCauseAnalysis.fiveWhys || [
            { level: 1, question: 'Why does this problem occur?', answer: '' },
            { level: 2, question: 'Why?', answer: '' },
            { level: 3, question: 'Why?', answer: '' },
            { level: 4, question: 'Why?', answer: '' },
            { level: 5, question: 'What is the root cause?', answer: '' }
          ],
          barriersToInternalSolution: rootCauseAnalysis.barriersToInternalSolution || [],
          hypothesis: solutionPotential.hypothesis || '',
          solutionType: solutionPotential.solutionType || 'hybrid',
          keyFeatures: solutionPotential.keyFeatures || [],
          mvpFeatures: solutionPotential.mvpFeatures || [],
          estimatedDevCost: solutionPotential.estimatedDevCost || 0,
          estimatedDevTimeline: solutionPotential.estimatedDevTimeline || 0,
          estimatedDeployCost: solutionPotential.estimatedDeployCost || 0,
          estimatedPriceModel: solutionPotential.estimatedPriceModel || 'Subscription',
          anchorPaybackMonths: solutionPotential.anchorPaybackMonths || 0,
          marketSizeKenya: solutionPotential.marketSizeKenya || 0,
          marketSizeEastAfrica: solutionPotential.marketSizeEastAfrica || 0,
          competitorLandscape: solutionPotential.competitorLandscape || '',
          scalabilityScore: solutionPotential.scalabilityScore || 3,
          anchorUrgencyScore: verification.anchorUrgency || 3,
          willingToPay: verification.willingToPay || 'maybe',
          loiPotential: verification.loiPotential || 2
        });

        setProblemData(data);
      }
    } catch (error) {
      console.error('Error loading problem data:', error);
    }
  };

  useEffect(() => {
    const loadSubmission = async () => {
      setIsLoading(true);
      try {
        let sub: CompanySubmission | PublicSubmission | null = null;
        let convertedId: string | null = null;

        if (location.state?.submission) {
          sub = location.state.submission;
          convertedId = sub.convertedToProblemId || null;
          setIsRejected(sub.status === 'rejected');
        } 
        else if (id) {
          const { data, error } = await supabase
            .from('external_submissions')
            .select('*')
            .eq('id', id)
            .single();

          if (error) throw error;
          
          if (data) {
            const isCompany = data.company_name && data.company_name !== 'Individual Submission';
            convertedId = data.converted_to_problem_id || null;
            setIsRejected(data.status === 'rejected');
            
            sub = isCompany
              ? {
                  id: data.id,
                  type: 'company',
                  status: data.status,
                  submittedAt: data.submitted_at,
                  reviewedAt: data.reviewed_at,
                  reviewNotes: data.review_notes,
                  convertedToProblemId: data.converted_to_problem_id,
                  assignedTo: data.assigned_to,
                  assignedToName: data.assigned_to_name,
                  assignedAt: data.assigned_at,
                  companyName: data.company_name,
                  companyRegistration: data.company_registration,
                  industrySector: data.industry_sector,
                  contactName: data.contact_name,
                  contactTitle: data.contact_title || 'Contact',
                  contactEmail: data.contact_email,
                  contactPhone: data.contact_phone,
                  problemTitle: data.problem_title,
                  problemDescription: data.problem_description,
                  category: data.category || ['other'],
                  urgency: data.urgency,
                  estimatedImpact: data.estimated_impact,
                  totalAnnualCost: data.total_annual_cost || 0,
                  quantification: data.quantification,
                  rootCause: data.root_cause,
                  pilotReadiness: data.pilot_readiness,
                  technicalInfo: data.technical_info,
                  documents: data.documents || []
                }
              : {
                  id: data.id,
                  type: 'public',
                  status: data.status,
                  submittedAt: data.submitted_at,
                  reviewedAt: data.reviewed_at,
                  reviewNotes: data.review_notes,
                  convertedToProblemId: data.converted_to_problem_id,
                  assignedTo: data.assigned_to,
                  assignedToName: data.assigned_to_name,
                  assignedAt: data.assigned_at,
                  fullName: data.contact_name,
                  email: data.contact_email,
                  phone: data.contact_phone,
                  problemTitle: data.problem_title,
                  problemDescription: data.problem_description,
                  industrySector: data.industry_sector,
                  category: data.category || ['other'],
                  urgency: data.urgency,
                  location: data.location,
                  estimatedImpact: data.estimated_impact,
                  documents: data.documents || []
                };
          }
        }

        if (sub) {
          setSubmission(sub);
          setReviewNotes(sub.reviewNotes || '');
          
          if (convertedId) {
            setIsConverted(true);
            setConvertedProblemId(convertedId);
            await loadProblemData(convertedId);
          } else if (sub.status !== 'rejected') {
            let rootCauseDetailsValue = 'Needs investigation';
            if (sub.type === 'company') {
              const companySub = sub as CompanySubmission;
              if (companySub.rootCause) {
                rootCauseDetailsValue = getRootCauseDetails(companySub.rootCause);
              }
            }
            
            setEnrichment(prev => ({
              ...prev,
              totalLossPerYear: sub.type === 'company' ? (sub as CompanySubmission).totalAnnualCost || 0 : 0,
              calculationMethodology: sub.estimatedImpact || 'From external submission data',
              dataSources: ['External submission'],
              rootCauseDetails: rootCauseDetailsValue,
              anchorUrgencyScore: sub.urgency === 'high' ? 4 : sub.urgency === 'medium' ? 3 : 2,
              willingToPay: sub.urgency === 'high' ? 'yes' : 'maybe',
            }));
          }
        }
        
        await loadAvailableAnalysts();
      } catch (error) {
        console.error('Error loading submission:', error);
        toast({
          title: 'Error',
          description: 'Failed to load submission',
          variant: 'destructive'
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSubmission();
  }, [id, location.state, toast]);

  const getNextProblemNumber = async () => {
    const { data } = await supabase
      .from('problem_bank')
      .select('problem_id')
      .not('problem_id', 'is', null)
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (data && data.length > 0 && data[0].problem_id) {
      const lastId = data[0].problem_id;
      const match = lastId.match(/\d+$/);
      if (match) {
        const nextNum = parseInt(match[0]) + 1;
        return `VV-${nextNum.toString().padStart(3, '0')}`;
      }
    }
    return 'VV-001';
  };

  const validateEnrichment = (): boolean => {
    const errors: string[] = [];
    
    if (enrichment.totalLossPerYear <= 0) {
      errors.push('Please enter a valid annual loss amount');
    }
    
    if (!enrichment.calculationMethodology || enrichment.calculationMethodology.trim() === '') {
      errors.push('Please describe the calculation methodology');
    }
    
    if (!enrichment.rootCauseDetails || enrichment.rootCauseDetails.trim() === '') {
      errors.push('Please provide root cause details');
    }
    
    if (!enrichment.hypothesis || enrichment.hypothesis.trim() === '') {
      errors.push('Please provide a solution hypothesis');
    }
    
    if (enrichment.keyFeatures.length === 0) {
      errors.push('Please add at least one key feature');
    }
    
    if (enrichment.mvpFeatures.length === 0) {
      errors.push('Please add at least one MVP feature');
    }
    
    if (enrichment.estimatedDevCost <= 0) {
      errors.push('Please enter an estimated development cost');
    }
    
    if (enrichment.anchorPaybackMonths <= 0) {
      errors.push('Please enter estimated payback months');
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleReassign = async () => {
    if (!submission || !selectedAnalystId) return;

    setIsReassigning(true);
    try {
      const analyst = availableAnalysts.find(a => a.id === selectedAnalystId);
      
      const { error } = await supabase
        .from('external_submissions')
        .update({
          assigned_to: selectedAnalystId,
          assigned_to_name: analyst?.name,
          assigned_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', submission.id);

      if (error) throw error;

      toast({
        title: 'Analyst Reassigned',
        description: `Submission reassigned to ${analyst?.name}`,
      });

      setReassignmentDialogOpen(false);
      setSelectedAnalystId('');
      
      setSubmission({
        ...submission,
        assignedTo: selectedAnalystId,
        assignedToName: analyst?.name,
        assignedAt: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error reassigning:', error);
      toast({
        title: 'Error',
        description: 'Failed to reassign submission',
        variant: 'destructive'
      });
    } finally {
      setIsReassigning(false);
    }
  };

  const handleConvertToProblem = async () => {
    if (!submission) return;
    
    if (isConverted || submission.convertedToProblemId) {
      toast({ 
        title: 'Already Converted', 
        description: 'This submission has already been converted',
        variant: 'destructive' 
      });
      return;
    }
    
    if (isRejected || submission.status === 'rejected') {
      toast({ 
        title: 'Cannot Convert', 
        description: 'This submission has already been rejected and cannot be converted',
        variant: 'destructive' 
      });
      return;
    }
    
    if (!validateEnrichment()) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields before converting',
        variant: 'destructive'
      });
      setActiveTab('quantification');
      return;
    }

    setIsSubmitting(true);
    try {
      const { data: currentSubmission, error: checkError } = await supabase
        .from('external_submissions')
        .select('converted_to_problem_id, status')
        .eq('id', submission.id)
        .single();
        
      if (checkError) throw checkError;
      
      if (currentSubmission?.converted_to_problem_id) {
        toast({ 
          title: 'Already Converted', 
          description: 'This submission has already been converted',
          variant: 'destructive' 
        });
        setIsConverted(true);
        return;
      }
      
      if (currentSubmission?.status === 'rejected') {
        toast({ 
          title: 'Cannot Convert', 
          description: 'This submission has been rejected',
          variant: 'destructive' 
        });
        return;
      }
      
      const problemId = await getNextProblemNumber();
      
      let categories = submission.type === 'company' 
        ? (submission as CompanySubmission).category 
        : (submission as PublicSubmission).category;
      
      if (!categories || categories.length === 0) {
        const title = submission.problemTitle.toLowerCase();
        if (title.includes('labour') || title.includes('worker') || title.includes('harvest')) {
          categories = ['labor_efficiency'];
        } else if (title.includes('cold') || title.includes('transport')) {
          categories = ['logistics'];
        } else if (title.includes('energy') || title.includes('air leak')) {
          categories = ['energy_waste'];
        } else if (title.includes('quality') || title.includes('defect')) {
          categories = ['quality_control'];
        } else {
          categories = ['other'];
        }
      }
      
      let industrySector = submission.type === 'company'
        ? (submission as CompanySubmission).industrySector
        : (submission as PublicSubmission).industrySector;
      
      if (!industrySector) {
        const title = submission.problemTitle.toLowerCase();
        if (title.includes('avocado') || title.includes('harvest') || title.includes('agricultural')) {
          industrySector = 'agro_processing';
        } else if (title.includes('manufacturing')) {
          industrySector = 'fmcg';
        } else {
          industrySector = 'other';
        }
      }
      
      const problemData = {
        problem_id: problemId,
        date_discovered: new Date().toISOString().split('T')[0],
        source: submission.type === 'company' ? 'company_submission' : 'public_submission',
        title: submission.problemTitle,
        category: categories,
        industry_sector: industrySector,
        status: 'lead',
        anchor: {
          name: submission.type === 'company' 
            ? (submission as CompanySubmission).companyName 
            : (submission as PublicSubmission).fullName,
          location: submission.type === 'public' 
            ? (submission as PublicSubmission).location || 'Not specified'
            : 'Not specified',
          plantDepartment: 'General',
          contactPerson: {
            name: submission.type === 'company'
              ? (submission as CompanySubmission).contactName
              : (submission as PublicSubmission).fullName,
            email: submission.type === 'company'
              ? (submission as CompanySubmission).contactEmail
              : (submission as PublicSubmission).email,
            phone: submission.type === 'company'
              ? (submission as CompanySubmission).contactPhone
              : (submission as PublicSubmission).phone || 'Not provided'
          },
          relationshipStrength: 2,
          lastContactDate: new Date().toISOString().split('T')[0]
        },
        quantification: {
          description: submission.problemDescription,
          totalLossPerMonth: enrichment.totalLossPerYear / 12,
          totalLossPerYear: enrichment.totalLossPerYear,
          calculationMethodology: enrichment.calculationMethodology,
          dataSources: enrichment.dataSources,
          assumptions: [],
          confidenceLevel: enrichment.confidenceLevel
        },
        root_cause_analysis: {
          rootCauseCategory: enrichment.rootCauseCategory,
          rootCauseDetails: enrichment.rootCauseDetails,
          fiveWhys: enrichment.fiveWhys.filter(w => w.answer && w.answer.trim()),
          barriersToInternalSolution: enrichment.barriersToInternalSolution
        },
        solution_potential: {
          hypothesis: enrichment.hypothesis,
          solutionType: enrichment.solutionType,
          keyFeatures: enrichment.keyFeatures,
          mvpFeatures: enrichment.mvpFeatures,
          estimatedDevCost: enrichment.estimatedDevCost,
          estimatedDevTimeline: enrichment.estimatedDevTimeline,
          estimatedDeployCost: enrichment.estimatedDeployCost,
          estimatedPriceModel: enrichment.estimatedPriceModel,
          anchorPaybackMonths: enrichment.anchorPaybackMonths,
          marketSizeKenya: enrichment.marketSizeKenya,
          marketSizeEastAfrica: enrichment.marketSizeEastAfrica,
          competitorLandscape: enrichment.competitorLandscape,
          scalabilityScore: enrichment.scalabilityScore
        },
        verification: {
          observedFirsthand: true,
          anchorUrgency: enrichment.anchorUrgencyScore,
          willingToPay: enrichment.willingToPay,
          loiPotential: enrichment.loiPotential,
          photoVideoEvidence: submission.documents || [],
          dataFilesReceived: [],
          anchorAcknowledgment: {
            obtained: false,
            method: 'pending'
          }
        },
        created_by: user?.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: problem, error: problemError } = await supabase
        .from('problem_bank')
        .insert(problemData)
        .select()
        .single();

      if (problemError) {
        console.error('Problem insert error:', problemError);
        toast({ 
          title: 'Error', 
          description: `Failed to create problem: ${problemError.message}`,
          variant: 'destructive' 
        });
        return;
      }

      const { error: updateError } = await supabase
        .from('external_submissions')
        .update({ 
          converted_to_problem_id: problem.id,
          status: 'converted',
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes,
          reviewed_by: user?.id
        })
        .eq('id', submission.id);

      if (updateError) {
        console.error('Update error:', updateError);
        toast({ 
          title: 'Partial Success', 
          description: `Problem created (${problem.problem_id}) but submission link failed.`,
          variant: 'default' 
        });
      } else {
        toast({ 
          title: 'Success!', 
          description: `"${submission.problemTitle}" converted to Problem Bank as LEAD (ID: ${problem.problem_id}).`
        });
      }
      
      setIsConverted(true);
      setConvertedProblemId(problem.id);
      await loadProblemData(problem.id);
      setSubmission(prev => prev ? { ...prev, convertedToProblemId: problem.id } : null);
      
    } catch (error) {
      console.error('Error converting submission:', error);
      toast({ 
        title: 'Error', 
        description: error instanceof Error ? error.message : 'Failed to convert submission',
        variant: 'destructive' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!submission) return;
    
    if (isConverted || submission.convertedToProblemId) {
      toast({ 
        title: 'Already Converted', 
        description: 'This submission has already been converted',
        variant: 'destructive' 
      });
      return;
    }
    
    if (isRejected || submission.status === 'rejected') {
      toast({ 
        title: 'Already Rejected', 
        description: 'This submission has already been rejected',
        variant: 'destructive' 
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error: updateError } = await supabase
        .from('external_submissions')
        .update({
          status: 'rejected',
          review_notes: reviewNotes,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user?.id
        })
        .eq('id', submission.id);

      if (updateError) throw updateError;

      toast({
        title: 'Submission Rejected',
        description: `"${submission.problemTitle}" has been rejected. Review notes have been saved.`,
      });
      
      setIsRejected(true);
      setSubmission(prev => prev ? { ...prev, status: 'rejected', reviewedAt: new Date().toISOString(), reviewNotes: reviewNotes } : null);
      
    } catch (error) {
      console.error('Error rejecting submission:', error);
      toast({
        title: 'Error',
        description: 'Failed to reject submission',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value: number) => {
    if (!value) return 'KES 0';
    if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
    if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
    return `KES ${value.toLocaleString()}`;
  };

  const getInitials = (name: string) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getProgress = () => {
    const requiredFields = [
      enrichment.totalLossPerYear > 0,
      enrichment.calculationMethodology && enrichment.calculationMethodology.trim() !== '',
      enrichment.rootCauseDetails && enrichment.rootCauseDetails.trim() !== '',
      enrichment.hypothesis && enrichment.hypothesis.trim() !== '',
      enrichment.keyFeatures.length > 0,
      enrichment.mvpFeatures.length > 0,
      enrichment.estimatedDevCost > 0,
      enrichment.anchorPaybackMonths > 0
    ];
    const completed = requiredFields.filter(Boolean).length;
    return Math.round((completed / requiredFields.length) * 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <AlertTriangle className="h-12 w-12 text-red-500" />
        <h2 className="text-xl font-semibold">Submission Not Found</h2>
        <p className="text-muted-foreground">The submission you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/problem-bank/submissions')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Submissions
        </Button>
      </div>
    );
  }

  const isRejectedStatus = isRejected || submission.status === 'rejected';
  const isConvertedStatus = isConverted || submission.convertedToProblemId;
  const isAssignedToCurrentUser = submission.assignedTo === user?.id;
  const canReview = isProblemBankAnalyst && (isAssignedToCurrentUser || isSeniorAnalyst) && !isRejectedStatus && !isConvertedStatus;

  return (
    <div className="w-full min-h-screen bg-background">
      <div className="w-full px-6 py-6">
        {/* Header */}
        <div className="w-full mb-6">
          <div className="flex flex-wrap items-center gap-4 w-full">
            <Button variant="ghost" size="icon" onClick={() => navigate('/problem-bank/submissions')} className="shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight break-words">
                {isRejectedStatus ? 'Rejected Submission' : isConvertedStatus ? 'Converted Problem' : 'Review Submission'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1 break-words">
                {isRejectedStatus 
                  ? `This submission was rejected on ${new Date(submission.reviewedAt || '').toLocaleDateString()}.`
                  : isConvertedStatus 
                  ? `This problem was converted to Problem Bank as LEAD (ID: ${problemData?.problem_id || convertedProblemId}).`
                  : 'Review and enrich problem details before converting to Problem Bank.'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {isRejectedStatus && (
                <Badge className="bg-red-100 text-red-700 whitespace-nowrap">
                  <XCircle className="h-3 w-3 mr-1" />
                  REJECTED
                </Badge>
              )}
              {isConvertedStatus && !isRejectedStatus && (
                <Badge className="bg-green-100 text-green-700 whitespace-nowrap">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  CONVERTED
                </Badge>
              )}
              {!isRejectedStatus && !isConvertedStatus && (
                <Badge variant="outline" className={`${URGENCY_COLORS[submission.urgency]} whitespace-nowrap`}>
                  {submission.urgency.toUpperCase()} URGENCY
                </Badge>
              )}
              <Badge variant="outline" className="bg-blue-100 text-blue-700 whitespace-nowrap">
                {submission.type === 'company' ? 'Company Submission' : 'Public Submission'}
              </Badge>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="shrink-0">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Assignment Info Card */}
        {!isRejectedStatus && (
          <Card className="mb-6 border-purple-200 bg-purple-50/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 p-2 rounded-full">
                    <UserCheck className="h-5 w-5 text-purple-700" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Assigned Analyst</p>
                    {submission.assignedToName ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs bg-purple-100 text-purple-700">
                            {getInitials(submission.assignedToName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{submission.assignedToName}</span>
                        {submission.assignedAt && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1 ml-2">
                            <Calendar className="h-3 w-3" />
                            Assigned {new Date(submission.assignedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-red-600 mt-1">No analyst assigned</p>
                    )}
                  </div>
                </div>
                
                {isSeniorAnalyst && !isConvertedStatus && !isRejectedStatus && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setReassignmentDialogOpen(true)}
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Reassign Analyst
                  </Button>
                )}
              </div>
              
              {!canReview && !isSeniorAnalyst && submission.assignedTo && submission.assignedTo !== user?.id && !isConvertedStatus && !isRejectedStatus && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-700 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    This submission is assigned to {submission.assignedToName}. Only the assigned analyst can review and convert it.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Progress Bar */}
        {!isRejectedStatus && !isConvertedStatus && isAssignedToCurrentUser && (
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span>Enrichment Progress</span>
              <span>{getProgress()}% Complete</span>
            </div>
            <Progress value={getProgress()} className="h-2" />
          </div>
        )}

        {/* Not Authorized Message */}
        {!canReview && !isSeniorAnalyst && submission.assignedTo && submission.assignedTo !== user?.id && !isConvertedStatus && !isRejectedStatus && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-200">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <AlertTitle className="text-yellow-800">Access Restricted</AlertTitle>
            <AlertDescription className="text-yellow-700">
              This submission is assigned to {submission.assignedToName}. Only the assigned analyst can review and convert this submission.
              {isSeniorAnalyst && " As a Senior Analyst, you can reassign it if needed."}
            </AlertDescription>
          </Alert>
        )}

        {/* Converted Alert */}
        {isConvertedStatus && !isRejectedStatus && problemData && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">Successfully Converted to Problem Bank</AlertTitle>
            <AlertDescription className="text-green-700">
              <p>Problem ID: <strong>{problemData.problem_id}</strong></p>
              <p className="text-sm mt-1">Status: <Badge className="bg-yellow-100 text-yellow-700 mt-1">LEAD</Badge></p>
            </AlertDescription>
          </Alert>
        )}

        {/* Rejected Alert */}
        {isRejectedStatus && (
          <Alert className="mb-6 bg-red-50 border-red-200">
            <XCircle className="h-4 w-4 text-red-600" />
            <AlertTitle className="text-red-800">Submission Rejected</AlertTitle>
            <AlertDescription className="text-red-700">
              <p>This submission was rejected on {new Date(submission.reviewedAt || '').toLocaleDateString()}.</p>
              {submission.reviewNotes && (
                <p className="text-sm mt-2">Reason: {submission.reviewNotes}</p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Validation Errors Alert */}
        {validationErrors.length > 0 && !isConvertedStatus && !isRejectedStatus && canReview && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Missing Required Fields</AlertTitle>
            <AlertDescription>
              <ul className="list-disc list-inside mt-2">
                {validationErrors.map((err, i) => (
                  <li key={i} className="text-sm">{err}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
          {/* Left Column - Original Submission */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  Original Submission
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Submitter</p>
                  <div className="flex items-center gap-2 mt-1">
                    {submission.type === 'company' ? (
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <User className="h-4 w-4 text-muted-foreground" />
                    )}
                    <p className="font-medium break-words">
                      {submission.type === 'company' 
                        ? (submission as CompanySubmission).companyName 
                        : (submission as PublicSubmission).fullName}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground">Contact</p>
                  <div className="space-y-1 mt-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-3 w-3 text-muted-foreground" />
                      <span className="break-all">{submission.type === 'company' ? (submission as CompanySubmission).contactEmail : (submission as PublicSubmission).email}</span>
                    </div>
                    {(submission.type === 'company' ? (submission as CompanySubmission).contactPhone : (submission as PublicSubmission).phone) && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-3 w-3 text-muted-foreground" />
                        <span>{submission.type === 'company' ? (submission as CompanySubmission).contactPhone : (submission as PublicSubmission).phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {submission.type === 'public' && (submission as PublicSubmission).location && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Location</p>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">{(submission as PublicSubmission).location}</span>
                    </div>
                  </div>
                )}

                {(submission.type === 'company' ? (submission as CompanySubmission).industrySector : (submission as PublicSubmission).industrySector) && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Industry Sector</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Briefcase className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">
                        {submission.type === 'company' 
                          ? (submission as CompanySubmission).industrySector 
                          : (submission as PublicSubmission).industrySector}
                      </span>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium text-muted-foreground">Categories</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(submission.category || []).map((cat, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Separator />

                <div>
                  <p className="text-sm font-medium text-muted-foreground">Problem Title</p>
                  <p className="font-semibold mt-1 break-words">{submission.problemTitle}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-muted-foreground">Description</p>
                  <div className="mt-2 p-3 bg-muted/30 rounded-md">
                    <p className="text-sm whitespace-pre-wrap break-words">{submission.problemDescription}</p>
                  </div>
                </div>

                {submission.documents && submission.documents.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Attached Documents</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {submission.documents.map((doc, i) => (
                        <a
                          key={i}
                          href={doc}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <FileText className="h-3 w-3" />
                          Document {i + 1}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Review Notes Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Review Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add your review notes, analysis, and comments here..."
                  rows={6}
                  disabled={isConvertedStatus || isRejectedStatus || !canReview}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Enrichment Forms */}
          {!isRejectedStatus && (
            <div className="lg:col-span-2 space-y-6">
              {canReview ? (
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
                    <TabsTrigger value="quantification">💰 Quantification</TabsTrigger>
                    <TabsTrigger value="root-cause">🔍 Root Cause</TabsTrigger>
                    <TabsTrigger value="solution">💡 Solution</TabsTrigger>
                    <TabsTrigger value="verification">✅ Verification</TabsTrigger>
                  </TabsList>

                  {/* Quantification Tab */}
                  <TabsContent value="quantification" className="space-y-4 pt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Financial Impact</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="flex items-center gap-1">Annual Loss (KES) <span className="text-red-500">*</span></Label>
                            <Input
                              type="number"
                              value={enrichment.totalLossPerYear}
                              onChange={(e) => setEnrichment({...enrichment, totalLossPerYear: parseFloat(e.target.value) || 0})}
                              placeholder="e.g., 165000000"
                              disabled={isConvertedStatus}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Confidence Level <span className="text-red-500">*</span></Label>
                            <Select value={enrichment.confidenceLevel} onValueChange={(v: any) => setEnrichment({...enrichment, confidenceLevel: v})} disabled={isConvertedStatus}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {CONFIDENCE_LEVELS.map(level => (
                                  <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Calculation Methodology <span className="text-red-500">*</span></Label>
                          <Textarea
                            value={enrichment.calculationMethodology}
                            onChange={(e) => setEnrichment({...enrichment, calculationMethodology: e.target.value})}
                            placeholder="Describe how the loss was calculated..."
                            rows={3}
                            disabled={isConvertedStatus}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Data Sources</Label>
                          <div className="flex gap-2">
                            <Input
                              value={tempDataSource}
                              onChange={(e) => setTempDataSource(e.target.value)}
                              placeholder="Add data source..."
                              className="flex-1"
                              disabled={isConvertedStatus}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                if (tempDataSource.trim()) {
                                  setEnrichment({
                                    ...enrichment,
                                    dataSources: [...enrichment.dataSources, tempDataSource.trim()]
                                  });
                                  setTempDataSource('');
                                }
                              }}
                              disabled={isConvertedStatus}
                            >
                              <PlusCircle className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {enrichment.dataSources.map((source, i) => (
                              <Badge key={i} variant="secondary" className="gap-1">
                                {source}
                                {!isConvertedStatus && (
                                  <XCircle
                                    className="h-3 w-3 cursor-pointer"
                                    onClick={() => setEnrichment({
                                      ...enrichment,
                                      dataSources: enrichment.dataSources.filter((_, idx) => idx !== i)
                                    })}
                                  />
                                )}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Root Cause Tab */}
                  <TabsContent value="root-cause" className="space-y-4 pt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Root Cause Analysis</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Root Cause Category <span className="text-red-500">*</span></Label>
                          <Select value={enrichment.rootCauseCategory} onValueChange={(v) => setEnrichment({...enrichment, rootCauseCategory: v})} disabled={isConvertedStatus}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {ROOT_CAUSE_CATEGORIES.map(cat => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Root Cause Details <span className="text-red-500">*</span></Label>
                          <Textarea
                            value={enrichment.rootCauseDetails}
                            onChange={(e) => setEnrichment({...enrichment, rootCauseDetails: e.target.value})}
                            placeholder="Describe the root cause in detail..."
                            rows={3}
                            disabled={isConvertedStatus}
                          />
                        </div>
                        
                        <div className="space-y-3">
                          <Label>5 Whys Analysis</Label>
                          {enrichment.fiveWhys.map((why, idx) => (
                            <div key={idx} className="pl-4 border-l-2 border-blue-200">
                              <p className="text-sm font-medium text-blue-600">Why {why.level}?</p>
                              <Input
                                value={why.answer}
                                onChange={(e) => {
                                  const newFiveWhys = [...enrichment.fiveWhys];
                                  newFiveWhys[idx].answer = e.target.value;
                                  setEnrichment({...enrichment, fiveWhys: newFiveWhys});
                                }}
                                placeholder={why.question}
                                className="mt-1"
                                disabled={isConvertedStatus}
                              />
                            </div>
                          ))}
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Barriers to Internal Solution</Label>
                          <div className="flex gap-2">
                            <Input
                              value={tempBarrier}
                              onChange={(e) => setTempBarrier(e.target.value)}
                              placeholder="Add barrier..."
                              className="flex-1"
                              disabled={isConvertedStatus}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                if (tempBarrier.trim()) {
                                  setEnrichment({
                                    ...enrichment,
                                    barriersToInternalSolution: [...enrichment.barriersToInternalSolution, tempBarrier.trim()]
                                  });
                                  setTempBarrier('');
                                }
                              }}
                              disabled={isConvertedStatus}
                            >
                              <PlusCircle className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {enrichment.barriersToInternalSolution.map((barrier, i) => (
                              <Badge key={i} variant="secondary" className="gap-1">
                                {barrier}
                                {!isConvertedStatus && (
                                  <XCircle
                                    className="h-3 w-3 cursor-pointer"
                                    onClick={() => setEnrichment({
                                      ...enrichment,
                                      barriersToInternalSolution: enrichment.barriersToInternalSolution.filter((_, idx) => idx !== i)
                                    })}
                                  />
                                )}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Solution Tab */}
                  <TabsContent value="solution" className="space-y-4 pt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Solution Potential</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label>Solution Hypothesis <span className="text-red-500">*</span></Label>
                          <Textarea
                            value={enrichment.hypothesis}
                            onChange={(e) => setEnrichment({...enrichment, hypothesis: e.target.value})}
                            placeholder="What is the proposed solution?"
                            rows={2}
                            disabled={isConvertedStatus}
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Solution Type</Label>
                            <Select value={enrichment.solutionType} onValueChange={(v: any) => setEnrichment({...enrichment, solutionType: v})} disabled={isConvertedStatus}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {SOLUTION_TYPES.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Estimated Dev Cost (KES) <span className="text-red-500">*</span></Label>
                            <Input
                              type="number"
                              value={enrichment.estimatedDevCost}
                              onChange={(e) => setEnrichment({...enrichment, estimatedDevCost: parseFloat(e.target.value) || 0})}
                              disabled={isConvertedStatus}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Dev Timeline (months)</Label>
                            <Input
                              type="number"
                              value={enrichment.estimatedDevTimeline}
                              onChange={(e) => setEnrichment({...enrichment, estimatedDevTimeline: parseFloat(e.target.value) || 0})}
                              disabled={isConvertedStatus}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Payback Period (months) <span className="text-red-500">*</span></Label>
                            <Input
                              type="number"
                              value={enrichment.anchorPaybackMonths}
                              onChange={(e) => setEnrichment({...enrichment, anchorPaybackMonths: parseFloat(e.target.value) || 0})}
                              disabled={isConvertedStatus}
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Key Features <span className="text-red-500">*</span></Label>
                          <div className="flex gap-2">
                            <Input
                              value={tempKeyFeature}
                              onChange={(e) => setTempKeyFeature(e.target.value)}
                              placeholder="Add key feature..."
                              className="flex-1"
                              disabled={isConvertedStatus}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                if (tempKeyFeature.trim()) {
                                  setEnrichment({
                                    ...enrichment,
                                    keyFeatures: [...enrichment.keyFeatures, tempKeyFeature.trim()]
                                  });
                                  setTempKeyFeature('');
                                }
                              }}
                              disabled={isConvertedStatus}
                            >
                              <PlusCircle className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {enrichment.keyFeatures.map((feature, i) => (
                              <Badge key={i} variant="secondary" className="gap-1">
                                {feature}
                                {!isConvertedStatus && (
                                  <XCircle
                                    className="h-3 w-3 cursor-pointer"
                                    onClick={() => setEnrichment({
                                      ...enrichment,
                                      keyFeatures: enrichment.keyFeatures.filter((_, idx) => idx !== i)
                                    })}
                                  />
                                )}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>MVP Features <span className="text-red-500">*</span></Label>
                          <div className="flex gap-2">
                            <Input
                              value={tempMVPFeature}
                              onChange={(e) => setTempMVPFeature(e.target.value)}
                              placeholder="Add MVP feature..."
                              className="flex-1"
                              disabled={isConvertedStatus}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                if (tempMVPFeature.trim()) {
                                  setEnrichment({
                                    ...enrichment,
                                    mvpFeatures: [...enrichment.mvpFeatures, tempMVPFeature.trim()]
                                  });
                                  setTempMVPFeature('');
                                }
                              }}
                              disabled={isConvertedStatus}
                            >
                              <PlusCircle className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {enrichment.mvpFeatures.map((feature, i) => (
                              <Badge key={i} variant="secondary" className="gap-1">
                                {feature}
                                {!isConvertedStatus && (
                                  <XCircle
                                    className="h-3 w-3 cursor-pointer"
                                    onClick={() => setEnrichment({
                                      ...enrichment,
                                      mvpFeatures: enrichment.mvpFeatures.filter((_, idx) => idx !== i)
                                    })}
                                  />
                                )}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Market Size (Kenya)</Label>
                            <Input
                              type="number"
                              value={enrichment.marketSizeKenya}
                              onChange={(e) => setEnrichment({...enrichment, marketSizeKenya: parseFloat(e.target.value) || 0})}
                              placeholder="Number of potential customers"
                              disabled={isConvertedStatus}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Market Size (East Africa)</Label>
                            <Input
                              type="number"
                              value={enrichment.marketSizeEastAfrica}
                              onChange={(e) => setEnrichment({...enrichment, marketSizeEastAfrica: parseFloat(e.target.value) || 0})}
                              disabled={isConvertedStatus}
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Competitor Landscape</Label>
                          <Textarea
                            value={enrichment.competitorLandscape}
                            onChange={(e) => setEnrichment({...enrichment, competitorLandscape: e.target.value})}
                            placeholder="Describe competitors and their solutions..."
                            rows={2}
                            disabled={isConvertedStatus}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Verification Tab */}
                  <TabsContent value="verification" className="space-y-4 pt-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Verification & Commitment</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Anchor Urgency (1-5)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              value={enrichment.anchorUrgencyScore}
                              onChange={(e) => setEnrichment({...enrichment, anchorUrgencyScore: parseInt(e.target.value) || 3})}
                              disabled={isConvertedStatus}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Willing to Pay</Label>
                            <Select value={enrichment.willingToPay} onValueChange={(v: any) => setEnrichment({...enrichment, willingToPay: v})} disabled={isConvertedStatus}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="maybe">Maybe</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>LOI Potential (1-5)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="5"
                              value={enrichment.loiPotential}
                              onChange={(e) => setEnrichment({...enrichment, loiPotential: parseInt(e.target.value) || 2})}
                              disabled={isConvertedStatus}
                            />
                          </div>
                        </div>
                        
                        {!isConvertedStatus && !isRejectedStatus && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-700">
                              <strong>Note:</strong> After conversion, the problem will be created with status "LEAD".
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              ) : (
                <Card className="bg-gray-50">
                  <CardContent className="pt-12 pb-12 text-center">
                    <Shield className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-xl font-semibold mb-2">Access Restricted</h3>
                    <p className="text-muted-foreground">
                      {!submission.assignedTo 
                        ? "This submission hasn't been assigned to anyone yet."
                        : `This submission is assigned to ${submission.assignedToName}. Only the assigned analyst can review and convert it.`}
                    </p>
                    {isSeniorAnalyst && (
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => setReassignmentDialogOpen(true)}
                      >
                        Reassign Submission
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Show message when rejected */}
          {isRejectedStatus && (
            <div className="lg:col-span-2">
              <Card className="bg-red-50 border-red-200">
                <CardContent className="pt-12 pb-12 text-center">
                  <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
                  <h3 className="text-xl font-semibold text-red-700 mb-2">Submission Rejected</h3>
                  <p className="text-red-600 mb-4">This submission has been rejected and cannot be converted to a problem.</p>
                  {submission.reviewNotes && (
                    <div className="bg-white p-4 rounded-lg max-w-md mx-auto">
                      <p className="text-sm font-medium text-gray-700">Rejection Reason:</p>
                      <p className="text-sm text-gray-600 mt-1">{submission.reviewNotes}</p>
                    </div>
                  )}
                  <Button variant="outline" className="mt-6" onClick={() => navigate('/problem-bank/submissions')}>
                    Back to Submissions
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {!isRejectedStatus && canReview && (
          <div className="flex justify-end gap-3 pt-6 mt-6 border-t">
            <Button
              variant="outline"
              onClick={() => navigate('/problem-bank/submissions')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            
            {!isConvertedStatus && (
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ThumbsDown className="h-4 w-4 mr-2" />
                )}
                Reject Submission
              </Button>
            )}
            
            <Button
              onClick={handleConvertToProblem}
              disabled={isSubmitting || isConvertedStatus}
              className={isConvertedStatus ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"}
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : isConvertedStatus ? (
                <CheckCircle className="h-4 w-4 mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isConvertedStatus ? 'Already Converted' : 'Convert to Problem Bank (LEAD)'}
            </Button>
          </div>
        )}
      </div>

      {/* Reassignment Dialog */}
      <Dialog open={reassignmentDialogOpen} onOpenChange={setReassignmentDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Reassign Submission to Analyst</DialogTitle>
            <DialogDescription>
              Select a different Problem Bank Analyst to review this submission.
            </DialogDescription>
          </DialogHeader>
          
          {submission && (
            <div className="space-y-4 py-4">
              <div className="bg-muted/30 p-4 rounded-lg">
                <p className="font-medium">{submission.problemTitle}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Currently assigned to: <strong>{submission.assignedToName || 'No one'}</strong>
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Select New Analyst <span className="text-red-500">*</span></Label>
                <Select value={selectedAnalystId} onValueChange={setSelectedAnalystId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an analyst..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAnalysts.filter(a => a.id !== submission.assignedTo).map(analyst => (
                      <SelectItem key={analyst.id} value={analyst.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">
                              {getInitials(analyst.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span>{analyst.name}</span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            Problem Bank
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignmentDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReassign} 
              disabled={!selectedAnalystId || isReassigning}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isReassigning ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reassigning...</>
              ) : (
                <><UserCheck className="mr-2 h-4 w-4" />Reassign Submission</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}